import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { formatBalanceToAbel, SmartLink, toUTCDateTime } from '../../../abelcommon';
import { CenteredTableCell } from '../../../abelcommon/components/CenteredTableCell';
import { selectedMiningAccountState } from '../../../api';
import { ABELSCAN_BLOCK_URL } from '../../../api/abel';
import { LatestAllocation, loadBalancerAPI } from '../../../api/load-balancer';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

export const PendingListSection = () => {
  const dateFormat = 'yyyy-MM-dd HH:mm:ss';
  const selectedMiningAccount = useRecoilValue(selectedMiningAccountState);
  const { t } = useTranslation();

  const {
    data: latestBlockHeight,
    isLoading: latestBlockHeightLoading,
    error: latestBlockHeightError,
  } = loadBalancerAPI.hookWrapperQuery.useGetLatestBlockHeight();
  const {
    data: allocations,
    isLoading: allocationsLoading,
    error: allocationsError,
  } = loadBalancerAPI.hookWrapperQuery.useGetLatestAllocationInfo(selectedMiningAccount.id);

  return (
    <Card>
      <CardHeader title={t('pendingListSectionPage.current')} sx={{ flexWrap: 'wrap' }} />
      <CardContent sx={{ overflow: 'auto' }}>
        <TableContainer sx={{ minHeight: '100px', overflow: 'auto' }}>
          <Table sx={{ minWidth: '500px' }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('pendingListSectionPage.blockRange')}</TableCell>
                <TableCell>{t('pendingListSectionPage.estimatedABEL')}</TableCell>
                <TableCell>{t('pendingListSectionPage.share')} (%)</TableCell>
                <TableCell>
                  {t('pendingListSectionPage.shareCount')} / {t('pendingListSectionPage.totalCount')}
                </TableCell>
                <TableCell>{t('pendingListSectionPage.updatedAt')} (UTC)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {latestBlockHeightLoading || allocationsLoading ? (
                <TableRow>
                  <CenteredTableCell colSpan={5}>
                    <CircularProgress />
                  </CenteredTableCell>
                </TableRow>
              ) : latestBlockHeightError || allocationsError ? (
                <TableRow>
                  <CenteredTableCell colSpan={5}>
                    <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0, textAlign: 'center' }}>
                      {t('pendingListSectionPage.cannotGetMiningBlockRange')}
                    </Alert>
                  </CenteredTableCell>
                </TableRow>
              ) : !latestBlockHeight ||
                !(allocations.length > 0) ||
                !allocations ||
                allocations[0].startHeight > latestBlockHeight ||
                allocations[0].endHeight < latestBlockHeight ? (
                <TableRow>
                  <CenteredTableCell colSpan={5}>{t('global.noData')}</CenteredTableCell>
                </TableRow>
              ) : (
                <>
                  {allocations.map(
                    (allocation) =>
                      allocation.updatedAt &&
                      allocation.shareCount >= 0 && (
                        <AllocationRow key={allocation.startHeight} allocation={allocation} dateFormat={dateFormat} />
                      )
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const AllocationRow = ({ allocation, dateFormat }: { allocation: LatestAllocation; dateFormat: string }) => (
  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
    <TableCell component="th" scope="row">
      <SmartLink to={`${ABELSCAN_BLOCK_URL}/${allocation.startHeight}`}>{allocation.startHeight}</SmartLink>
      {' - '}
      <SmartLink to={`${ABELSCAN_BLOCK_URL}/${allocation.endHeight - 1}`}>{allocation.endHeight - 1}</SmartLink>
    </TableCell>
    <TableCell>
      {formatBalanceToAbel((allocation.totalReward * allocation.shareCount) / allocation.totalCount)}
    </TableCell>
    <TableCell>{((allocation.shareCount / allocation.totalCount) * 100).toFixed(2)}</TableCell>
    <TableCell>{`${allocation.shareCount} / ${allocation.totalCount}`}</TableCell>
    <TableCell>{format(toUTCDateTime(allocation.updatedAt), dateFormat)}</TableCell>
  </TableRow>
);
